// src/components/settings/PlatformSetupStatus.jsx - Unified platform setup components
import React from 'react';
import { useQuery, useMutation,useQueryClient } from 'react-query';
import { settingsAPI } from '../../services/api';
import toast from 'react-hot-toast';

// Generic Setup Status Component
const PlatformSetupStatus = ({
                                 platform,
                                 title,
                                 validationEndpoint,
                                 testEndpoint,
                                 credentials,
                                 setupInstructions,
                                 backgroundColor = 'bg-blue-50',
                                 borderColor = 'border-blue-200',
                                 textColor = 'text-blue-700',
                                 successColor = 'bg-green-50 border-green-200',
                                 errorColor = 'bg-red-50 border-red-200'
                             }) => {
    const queryClient = useQueryClient();

    // FIXED: Better cache management with manual refresh capability
    const {
        data: validation,
        isLoading,
        refetch: refetchValidation
    } = useQuery(
        `${platform}Validation`,
        () => validationEndpoint(),
        {
            enabled: true,
            staleTime: 30000, // 30 seconds - shorter for more responsive updates
            cacheTime: 120000, // 2 minutes - shorter cache time
            refetchOnWindowFocus: false,
            refetchInterval: false,
            retry: 1,
            onError: (error) => {
                // console.error(`${platform} validation error:`, error);
            },
            onSuccess: (data) => {
                // console.log(`✅ ${platform} validation successful:`, data?.data);
            }
        }
    );

    // FIXED: Enhanced test mutation with cache invalidation
    const testMutation = useMutation(testEndpoint, {
        onSuccess: (data) => {
            toast.success(`${title} test successful! ${data?.message || 'Check your account.'}`);

            // Invalidate and refetch validation after successful test
            queryClient.invalidateQueries(`${platform}Validation`);
            setTimeout(() => refetchValidation(), 1000);
        },
        onError: (error) => {
            console.error(`${platform} test error:`, error);
            toast.error(error.response?.data?.message || `${title} test failed`);
        }
    });

    // FIXED: Manual refresh function
    const handleRefreshValidation = async () => {
        toast.loading(`Refreshing ${title} validation...`, { id: 'validation-refresh' });

        try {
            // Invalidate cache and refetch
            await queryClient.invalidateQueries(`${platform}Validation`);
            await refetchValidation();

            toast.success(`${title} validation refreshed!`, { id: 'validation-refresh' });
        } catch (error) {
            toast.error(`Failed to refresh ${title} validation`, { id: 'validation-refresh' });
        }
    };

    if (isLoading) {
        return (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <div className="animate-pulse flex items-center">
                    <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                    <div className="ml-auto">
                        <div className="h-6 bg-gray-300 rounded w-16"></div>
                    </div>
                </div>
            </div>
        );
    }

    const validationData = validation?.data?.data || validation?.data;

    if (!validationData) {
        return (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center justify-between">
                    <p className="text-sm text-red-800">Failed to load {title} validation status</p>
                    <button
                        onClick={handleRefreshValidation}
                        className="text-xs text-red-600 hover:text-red-500 underline"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const isConfigured = validationData.isConfigured;

    return (
        <div className={`mt-4 p-4 ${isConfigured ? successColor : errorColor} border rounded-md`}>
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                        <h5 className={`text-sm font-medium ${isConfigured ? 'text-green-800' : 'text-red-800'}`}>
                            {isConfigured ? `✅ ${title} Ready` : `⚠️ ${title} Setup Required`}
                        </h5>

                        {/* ADDED: Manual refresh button */}
                        <button
                            onClick={handleRefreshValidation}
                            className="text-xs text-gray-500 hover:text-gray-700 underline"
                            title="Refresh validation status"
                        >
                            🔄 Refresh
                        </button>
                    </div>

                    <div className="text-xs space-y-1">
                        {credentials.map((cred) => {
                            const hasCredential = validationData[cred.key];
                            return (
                                <div key={cred.key} className="flex items-center space-x-2">
                                    <span className={hasCredential ? 'text-green-600' : 'text-red-600'}>
                                        {hasCredential ? '✓' : '✗'}
                                    </span>
                                    <span className="text-gray-700">{cred.label}</span>
                                    {/* ADDED: Show credential status */}
                                    <span className={`text-xs ${hasCredential ? 'text-green-500' : 'text-red-500'}`}>
                                        ({hasCredential ? 'Set' : 'Missing'})
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {!isConfigured && validationData.recommendations && (
                        <div className="mt-3 text-xs text-red-700">
                            <p className="font-medium">Missing:</p>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                                {validationData.recommendations
                                    .filter(rec => !rec.includes('✅'))
                                    .map((recommendation, index) => (
                                        <li key={index}>{recommendation}</li>
                                    ))}
                            </ul>
                        </div>
                    )}

                    {/* ADDED: Show available modes for LinkedIn */}
                    {platform === 'linkedin' && validationData.availableModes && validationData.availableModes.length > 0 && (
                        <div className="mt-3 text-xs text-green-700">
                            <p className="font-medium">Available posting modes:</p>
                            <ul className="list-disc list-inside mt-1">
                                {validationData.availableModes.map((mode, index) => (
                                    <li key={index} className="capitalize">
                                        {mode.replace('_', ' ')}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {isConfigured && (
                    <div className="ml-3 flex flex-col space-y-2">
                        <button
                            onClick={() => testMutation.mutate()}
                            disabled={testMutation.isLoading}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                            {testMutation.isLoading ? 'Testing...' : 'Test'}
                        </button>
                    </div>
                )}
            </div>

            {!isConfigured && setupInstructions && (
                <div className={`mt-3 text-xs ${textColor} ${backgroundColor} p-2 rounded`}>
                    <p className="font-medium">Quick Setup:</p>
                    {setupInstructions.map((instruction, index) => (
                        <p key={index}>{index + 1}. {instruction}</p>
                    ))}
                </div>
            )}

            {/* ADDED: Debug info in development */}
            {process.env.NODE_ENV === 'development' && (
                <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer">Debug Info</summary>
                    <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto">
                        {JSON.stringify(validationData, null, 2)}
                    </pre>
                </details>
            )}
        </div>
    );
};

// In PlatformSetupStatus.jsx
export const FacebookSetupStatus = () => {
    const { data: response, isLoading } = useQuery(
        'facebookValidation',
        settingsAPI.validateFacebook,
        {
            retry: false,
            refetchInterval: false,
        }
    );

    const validation = response?.data?.validation || {};
    const isReady = validation.isConfigured && validation.hasPageAccess;

    if (isLoading) {
        return <div className="animate-pulse">Checking Facebook...</div>;
    }

    return (
        <div className="mt-4 space-y-3">
            <h5 className="text-sm font-medium text-gray-700">
                {isReady ? '✅' : '⚠️'} Facebook Setup {isReady ? 'Complete' : 'Required'}
            </h5>

            <div className="space-y-2">
                <div className="flex items-center text-sm">
                    <span className={validation.hasAccessToken ? 'text-green-600' : 'text-red-600'}>
                        {validation.hasAccessToken ? '✓' : '✗'}
                    </span>
                    <span className="ml-2">Page Access Token</span>
                    {!validation.hasAccessToken && <span className="text-gray-500 ml-1">(Missing)</span>}
                </div>

                <div className="flex items-center text-sm">
                    <span className={validation.hasPageId ? 'text-green-600' : 'text-red-600'}>
                        {validation.hasPageId ? '✓' : '✗'}
                    </span>
                    <span className="ml-2">Page ID</span>
                    {!validation.hasPageId && <span className="text-gray-500 ml-1">(Missing)</span>}
                </div>

                {validation.pageInfo && (
                    <div className="flex items-center text-sm text-green-600">
                        <span>✓</span>
                        <span className="ml-2">Connected to: {validation.pageInfo.name}</span>
                    </div>
                )}
            </div>

            {validation.isConfigured && (
                <button
                    onClick={async () => {
                        try {
                            await settingsAPI.testFacebook();
                            toast.success('Facebook test post successful!');
                        } catch (error) {
                            toast.error(`Facebook test failed: ${error.response?.data?.message || error.message}`);
                        }
                    }}
                    className="mt-3 inline-flex items-center px-3 py-1 border border-green-300 text-xs font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
                >
                    Test Facebook Post
                </button>
            )}
        </div>
    );
};


// LinkedIn Setup Status - UPDATED with cache invalidation
export const LinkedInSetupStatus = () => {
    const queryClient = useQueryClient();

    // ADDED: Listen for credential updates to invalidate cache
    React.useEffect(() => {
        const handleCredentialUpdate = () => {
            // console.log('🔄 Credentials updated, invalidating LinkedIn validation cache');
            queryClient.invalidateQueries('linkedinValidation');
        };

        // You can emit this event when credentials are saved
        window.addEventListener('credentials-updated', handleCredentialUpdate);

        return () => {
            window.removeEventListener('credentials-updated', handleCredentialUpdate);
        };
    }, [queryClient]);

    return (
        <PlatformSetupStatus
            platform="linkedin"
            title="LinkedIn"
            validationEndpoint={() => settingsAPI.validateLinkedIn()}
            testEndpoint={() => settingsAPI.testLinkedIn()}
            credentials={[
                { key: 'hasPersonalToken', label: 'Personal Access Token' },
                { key: 'hasPersonalUrn', label: 'Personal URN' },
                { key: 'hasOrgToken', label: 'Organization Access Token (Optional)' },
                { key: 'hasOrgUrn', label: 'Organization URN (Optional)' }
            ]}
            setupInstructions={[
                'Use the LinkedIn Wizard in Settings to connect your account',
                'Or manually add credentials in the Credentials tab',
                'Get your URN with: node scripts/getLinkedInProfile.js'
            ]}
        />
    );
};

// Instagram Setup Status
export const InstagramSetupStatus = () => {
    return (
        <PlatformSetupStatus
            platform="instagram"
            title="Instagram"
            validationEndpoint={() => settingsAPI.validateInstagram()}
            testEndpoint={() => settingsAPI.testInstagram()}
            credentials={[
                { key: 'hasAccessToken', label: 'Access Token' },
                { key: 'hasBusinessAccountId', label: 'Business Account ID' }
            ]}
            setupInstructions={[
                <span key="1">Visit <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="underline">Facebook for Developers</a></span>,
                'Create an app and connect Instagram Business Account',
                'Generate long-lived access token',
                'Copy Business Account ID from Graph API Explorer'
            ]}
        />
    );
};

// Bluesky Setup Status
export const BlueSkySetupStatus = () => {
    return (
        <PlatformSetupStatus
            platform="bluesky"
            title="Bluesky"
            validationEndpoint={() => settingsAPI.validateBluesky()}
            testEndpoint={() => settingsAPI.testBluesky()}
            credentials={[
                { key: 'hasHandle', label: 'Handle' },
                { key: 'hasPassword', label: 'App Password' }
            ]}
            setupInstructions={[
                'Go to Bluesky Settings → App Passwords',
                'Create a new app password',
                'Enter your handle (without @) and app password',
                'Handle format: username.bsky.social'
            ]}
        />
    );
};

// Discord Setup Status
export const DiscordSetupStatus = () => {
    return (
        <PlatformSetupStatus
            platform="discord"
            title="Discord Notifications"
            validationEndpoint={() => settingsAPI.validateDiscord()}
            testEndpoint={() => settingsAPI.testDiscord()}
            credentials={[
                { key: 'hasWebhookUrl', label: 'Webhook URL' }
            ]}
            setupInstructions={[
                'Go to your Discord server settings',
                'Navigate to Integrations → Webhooks',
                'Create a new webhook or copy existing URL',
                'Paste the webhook URL in credentials'
            ]}
            backgroundColor="bg-purple-50"
            borderColor="border-purple-200"
            textColor="text-purple-700"
        />
    );
};

// Twitter Setup Status (Enhanced version of existing)
export const TwitterSetupStatus = () => {
    return (
        <PlatformSetupStatus
            platform="twitter"
            title="Twitter / X"
            validationEndpoint={() => settingsAPI.validateTwitter()}
            testEndpoint={() => settingsAPI.testTwitter()}
            credentials={[
                { key: 'hasApiKey', label: 'API Key' },
                { key: 'hasApiSecret', label: 'API Secret' },
                { key: 'hasAccessToken', label: 'Access Token' },
                { key: 'hasAccessTokenSecret', label: 'Access Token Secret' }
            ]}
            setupInstructions={[
                <span key="1">Visit <a href="https://developer.twitter.com/en/portal" target="_blank" rel="noopener noreferrer" className="underline">Twitter Developer Portal</a></span>,
                'Create app → Keys and Tokens → Copy credentials',
                'Ensure app has "Read and Write" permissions',
                'Add credentials in the Credentials tab'
            ]}
        />
    );
};